import { useState, useRef } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UploadSimple, FileCsv, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { InsuranceImportPanel } from "../components/InsuranceImportPanel";

const ENTITIES = [
  { value: "customers", label: "Customers" },
  { value: "vehicles", label: "Fleet / Vehicles" },
  { value: "chauffeurs", label: "Chauffeurs" },
  { value: "bookings", label: "Bookings / Rentals" },
  { value: "tours", label: "Tours" },
  { value: "leads", label: "Leads / Pre-bookings" },
  { value: "insurance", label: "Insurance cases (tracker sheet)" },
];

const COLUMN_HINTS = {
  customers: ["first_name", "last_name", "email", "phone", "company", "vip_tier", "address", "nationality", "license_number", "notes"],
  vehicles: ["make", "model", "year", "license_plate", "color", "vehicle_class", "status", "daily_rate", "mileage", "transmission", "seats", "insurance_expiry"],
  chauffeurs: ["first_name", "last_name", "email", "phone", "license_number", "languages", "rating", "status", "hire_date"],
  bookings: ["booking_type", "customer_name", "customer_phone", "customer_email", "vehicle_label", "start_datetime", "end_datetime", "pickup_location", "dropoff_location", "status", "total_amount"],
  tours: ["name", "description", "duration_days", "price_per_person", "max_capacity", "locations", "inclusions", "exclusions"],
  leads: ["customer_name", "customer_email", "customer_phone", "service_type", "pickup_date", "return_date", "vehicle_preference", "quoted_amount", "status", "source", "notes"],
  insurance: ["START DATE", "CLIENT", "MOST RECENT AUTH DATE", "VEHICLE PROVIDED", "FINAL END DATE", "AMOUNT", "OUR CUT", "AFFILIATE", "REFFERAL PAID", "AFF PAID (Y/N)", "CLIENT VEHICLE", "STATUS", "DETAILS TO GETTING PAID", "RATE BREAKDOWN", "ADJUSTER INFO / NOTES", "COMPANY", "CLAIM #", "METHOD OF DEPOSIT"],
};

export default function ImportData() {
  const [entity, setEntity] = useState("customers");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef();
  const isInsurance = entity === "insurance";

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please select a CSV file");
      return;
    }
    setFile(f);
    setResult(null);
  };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(`/import/${entity}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`Imported ${data.inserted} ${entity}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setUploading(false); }
  };

  return (
    <div className={`space-y-8 ${isInsurance ? "max-w-[1400px]" : ""}`} data-testid="import-page">
      <div className="border border-border bg-card p-8">
        <div className="label-mono">Step 1</div>
        <h2 className="font-heading text-2xl mt-1">Choose what you're importing</h2>
        <p className="text-sm text-muted-foreground mt-2">Headers in your CSV are auto-matched to fields. Common Google-Sheet column names (e.g. "Client Name", "Pickup", "Total") work out-of-the-box.</p>
        <div className="mt-5 max-w-sm">
          <Select value={entity} onValueChange={(v) => { setEntity(v); setResult(null); }}>
            <SelectTrigger data-testid="import-entity-select" className="bg-input border-border h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITIES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {isInsurance && (
          <p className="text-sm text-muted-foreground mt-4 max-w-2xl" data-testid="insurance-import-explainer">
            The tracker sheet goes through its own importer: a dry run first, so you read every row and its warnings
            before anything is written. Dates with no year are inferred from the row above and flagged. Nothing is
            emailed, no bookings or invoices are created, and rows already in the CRM are skipped as duplicates.
          </p>
        )}
        <div className="mt-5">
          <div className="label-mono mb-2">Supported columns for {entity}</div>
          <div className="flex flex-wrap gap-2">
            {COLUMN_HINTS[entity].map((c) => (
              <span key={c} className="text-xs px-2 py-1 border border-border bg-secondary/40 font-mono">{c}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="border border-border bg-card p-8">
        <div className="label-mono">Step 2</div>
        <h2 className="font-heading text-2xl mt-1">Upload your CSV</h2>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
          onClick={() => inputRef.current?.click()}
          data-testid="import-dropzone"
          className={`mt-6 border-2 border-dashed p-12 text-center cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
        >
          <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="import-file-input" />
          {file ? (
            <div className="space-y-2">
              <FileCsv size={40} className="mx-auto text-primary" />
              <div className="font-heading text-lg">{file.name}</div>
              <div className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(1)} KB · click to choose another</div>
            </div>
          ) : (
            <div className="space-y-2">
              <UploadSimple size={40} className="mx-auto text-muted-foreground" />
              <div className="font-heading text-lg">Drop your CSV here</div>
              <div className="text-xs text-muted-foreground">or click to browse</div>
            </div>
          )}
        </div>

        <div className="mt-6 flex gap-3">
          {isInsurance ? null : (
            <Button onClick={submit} disabled={!file || uploading} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="import-submit-button">
              {uploading ? "Importing…" : `Import to ${entity}`}
            </Button>
          )}
          {file && (
            <Button variant="outline" onClick={() => { setFile(null); setResult(null); }} data-testid="import-clear-button">
              Clear
            </Button>
          )}
        </div>

        {isInsurance && <div className="mt-6"><InsuranceImportPanel file={file} /></div>}
      </div>

      {result && !isInsurance && (
        <div className="border border-border bg-card p-8" data-testid="import-result">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle size={22} weight="fill" className="text-primary" />
            <h3 className="font-heading text-xl">Import complete</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4 max-w-md">
            <div className="border border-border p-4">
              <div className="label-mono">Inserted</div>
              <div className="font-heading text-3xl mt-1 text-primary">{result.inserted}</div>
            </div>
            <div className="border border-border p-4">
              <div className="label-mono">Failed</div>
              <div className="font-heading text-3xl mt-1 text-destructive">{result.failed}</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-6">
              <div className="label-mono mb-2 flex items-center gap-2"><WarningCircle size={14} /> First {result.errors.length} errors</div>
              <div className="space-y-1 text-xs font-mono">
                {result.errors.map((e, i) => (
                  <div key={`${e.row}-${i}`} className="text-muted-foreground">Row {e.row}: {e.error}</div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
